import { useCallback, useState } from 'react';
import { putToPresignedUrl, useRequestPresign } from '@/api/queries/uploads';
import { compressPhoto } from '@/utils/image';
import type { PresignRequest } from '@/api/types';

export type UploadPurpose = PresignRequest['purpose'];

interface UploadResult {
  objectKey: string;
}

interface UseUploadPhotoApi {
  upload: (file: File) => Promise<UploadResult>;
  uploading: boolean;
  error: string | null;
  reset: () => void;
}

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

// useUploadPhoto — presign → compress (≤1 MB) → PUT → resolve object_key.
//
// purpose maps directly to the backend enum (`passport_photo` | `avatar` |
// `support_attachment`). All photo uploads are compressed first via
// browser-image-compression — a 4 MB iPhone snap becomes a ~600-900 KB
// JPEG that the backend size-limit (10 MB) will gladly accept.
export function useUploadPhoto(purpose: UploadPurpose): UseUploadPhotoApi {
  const presign = useRequestPresign();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File): Promise<UploadResult> => {
      setUploading(true);
      setError(null);
      try {
        if (!ALLOWED_TYPES.has(file.type)) {
          throw new Error('errors.upload.bad_type');
        }
        const compressed = await compressPhoto(file);
        // After compression mime-type is normalised to image/jpeg unless
        // the source was image/png — both are in the backend enum.
        const contentType =
          compressed.type === 'image/png'
            ? 'image/png'
            : compressed.type === 'image/webp'
              ? 'image/webp'
              : 'image/jpeg';
        const sizeBytes = compressed.size;

        const ticket = await presign.mutateAsync({
          purpose,
          content_type: contentType,
          size_bytes: sizeBytes,
        });
        await putToPresignedUrl(ticket.upload_url, compressed, ticket.required_headers);
        return { objectKey: ticket.object_key };
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'errors.generic';
        setError(msg);
        throw e;
      } finally {
        setUploading(false);
      }
    },
    [purpose, presign],
  );

  const reset = useCallback(() => setError(null), []);

  return { upload, uploading, error, reset };
}
